import { useEffect, useState } from 'react';

// reactbits-style "carousel" (coverflow): the active image sits centre-stage,
// neighbours peek in on either side, scaled down + rotated in 3D + dimmed.
// Springy slide, click a side card to advance, arrows + dots, optional autoplay.
export default function GalleryCarousel({ images = [], accent = '#0D9488', autoPlay = true, interval = 4200 }) {
  const list = (images || []).filter(Boolean);
  const n = list.length;
  const [cur, setCur] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!autoPlay || paused || n <= 1) return undefined;
    const t = setInterval(() => setCur((i) => (i + 1) % n), interval);
    return () => clearInterval(t);
  }, [autoPlay, paused, n, interval]);

  if (!n) return null;
  const go = (d) => setCur((i) => (i + d + n) % n);

  // signed distance from the active slide, wrapping the short way round
  const rel = (i) => {
    let d = i - cur;
    if (d > n / 2) d -= n;
    if (d < -n / 2) d += n;
    return d;
  };

  const arrow = (side) => ({
    position: 'absolute', top: '50%', [side]: 14, transform: 'translateY(-50%)', zIndex: 200,
    width: 46, height: 46, borderRadius: '50%', border: 'none', cursor: 'pointer',
    background: 'rgba(255,255,255,0.94)', color: accent, fontSize: 26, lineHeight: 1,
    boxShadow: '0 6px 20px rgba(0,0,0,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      style={{ position: 'relative', width: '100%', height: 'clamp(250px, 42vw, 480px)', perspective: '1500px', overflow: 'hidden' }}>
      {list.map((src, i) => {
        const d = rel(i);
        const abs = Math.abs(d);
        if (abs > 2) return null; // only the nearest five are rendered
        const translateX = d * 52;
        const scale = d === 0 ? 1 : 0.82 - (abs - 1) * 0.07;
        const rotate = d === 0 ? 0 : d < 0 ? 20 : -20;
        return (
          <div key={i} onClick={() => d !== 0 && setCur(i)}
            style={{
              position: 'absolute', top: '50%', left: '50%', width: 'clamp(260px, 54%, 640px)', height: '88%',
              transform: `translate(-50%, -50%) translateX(${translateX}%) scale(${scale}) rotateY(${rotate}deg)`,
              transformStyle: 'preserve-3d',
              transition: 'transform 0.6s cubic-bezier(.22,.61,.36,1), opacity 0.6s',
              opacity: d === 0 ? 1 : 0.5, zIndex: 100 - abs,
              borderRadius: 18, overflow: 'hidden', cursor: d === 0 ? 'default' : 'pointer',
              boxShadow: d === 0 ? '0 30px 72px rgba(0,0,0,0.34)' : '0 12px 30px rgba(0,0,0,0.2)',
            }}>
            <img src={src} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            <div style={{ position: 'absolute', inset: 0, background: d === 0 ? 'transparent' : 'rgba(0,0,0,0.28)', transition: 'background 0.6s', pointerEvents: 'none' }} />
            {d === 0 && <div style={{ position: 'absolute', inset: 0, boxShadow: `inset 0 0 0 2px ${accent}66`, borderRadius: 18, pointerEvents: 'none' }} />}
          </div>
        );
      })}

      {n > 1 && (
        <>
          <button aria-label="Previous" onClick={() => go(-1)} style={arrow('left')}>‹</button>
          <button aria-label="Next" onClick={() => go(1)} style={arrow('right')}>›</button>
        </>
      )}

      <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 7, zIndex: 200 }}>
        {list.map((_, i) => (
          <button key={i} aria-label={`Go to ${i + 1}`} onClick={() => setCur(i)}
            style={{ width: i === cur ? 22 : 8, height: 8, borderRadius: 4, border: 'none', cursor: 'pointer', padding: 0, background: i === cur ? accent : 'rgba(255,255,255,0.6)', transition: 'all 0.3s' }} />
        ))}
      </div>
    </div>
  );
}
